import React, { useState } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import WipIncomingSubTable1 from "./incomingSubtables/WipIncomingSubTable1";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import CopyableIdentifier from "../../controls/CopyableIdentifier";
import Button from "../../controls/Button";
import NumberInputText from "../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import { isReadOnlyUser } from "../../../shared/utility";

export default function IncomingWipItemsTableComponent(props) {
  const {
    loading,
    values,
    openSlideForManufacturedItemDetails,
    onAskReturnIncomingItem,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    onWipItemTotalCostEditComplete,
    selectedItems = [],
    setSelectedItems,
    askReturnSelectedItems,
  } = props;

  const lotNumberBody = (item) => (
    <CopyableIdentifier value={item.lot_number} description="lot number" />
  );

  const [expandedRows, setExpandedRows] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();
  const isReadOnly = isReadOnlyUser();

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br />
        {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <NumberInputText
        value={item.total_weight_in_kg}
        aria-label={`Total weight for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={4}
        maxFractionDigits={4}
        suffix=" kg"
      />
    );
  };

  const bodyParentCartonCount = (item) => (
    <NumberInputText
      value={item.carton_count}
      aria-label={`Carton count for ${item.product_name}`}
      className="receiving-table-number"
      inputClassName="receiving-table-input"
      disabled
      minFractionDigits={0}
      maxFractionDigits={0}
      suffix=" cartons"
    />
  );

  const bodyParentTotalCost = (item) => {
    return (
      <NumberInputText
        value={item.total_cost}
        aria-label={`Total cost for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={isReadOnly}
        saveStatus={getSaveStatus(`incoming:${item.unique_id}:total_cost`)}
        onCommit={(value) => saveCell(`incoming:${item.unique_id}:total_cost`, () =>
          onWipItemTotalCostEditComplete({ ...item, total_cost: value, direction: "incoming" })
        )}
        mode="currency" currency="USD" locale="en-US" min={0}
        minFractionDigits={2} maxFractionDigits={2}
      />
    );
  };

  const bodyParentTotalPieces = (item) => {
    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label={`Total pieces for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={0}
        maxFractionDigits={0}
        suffix=" pieces"
      />
    );
  };

  const rowExpansionTemplate = (data) => {
    return (
      <WipIncomingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        openSlideForManufacturedItemDetails={
          openSlideForManufacturedItemDetails
        }
        onAskReturnIncomingItem={onAskReturnIncomingItem}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        isReadOnly={isReadOnly}
      />
    );
  };

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-2">
          <div className="w-full flex items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex-grow">
              Incoming items
            </h3>
            {!isReadOnly ? (
              <Button
                variant={selectedItems.length > 0 ? "danger" : "secondary"}
                disabled={selectedItems.length === 0}
                className="disabled:border-border disabled:bg-surface disabled:text-brand-muted disabled:opacity-100"
                onClick={askReturnSelectedItems}
              >
                Return ({selectedItems.length})
              </Button>
            ) : null}
          </div>
          {loading ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={values.incoming_items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="unique_id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm wip-incoming-items-table"
                cellClassName="p-2 border-1-bottom-gray text-sm"
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  header="Lot Number"
                  body={lotNumberBody}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Date"
                  field="date"
                  style={{ textAlign: "start", width: "10%" }}
                />
                <Column
                  header="Carton Count"
                  body={bodyParentCartonCount}
                  align="right"
                  alignHeader="right"
                  style={{ width: "15%" }}
                />
                <Column
                  header="Total Weight (kg)"
                  body={bodyParentTotalWeight}
                  align="right"
                  alignHeader="right"
                  style={{ width: "10%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentTotalPieces}
                  align="right"
                  alignHeader="right"
                  style={{ width: "10%" }}
                />
                <Column
                  header="Total Cost"
                  field="total_cost"
                  body={bodyParentTotalCost}
                  align="left"
                  alignHeader="left"
                  style={{ width: "10%" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              <div className="flex justify-end p-4 gap-4">
                {values.incoming_info ? (
                  <>
                    <p className="text-md font-bold">
                      Total cartons: {values.incoming_info.carton_count}
                    </p>
                    <p>
                      Total weight: {values.incoming_info.total_weight_in_kg} kg
                    </p>
                    <p>
                      Total pieces: {values.incoming_info.total_pieces} pieces
                    </p>
                    <p>Total cost: ${values.incoming_info.total_cost}</p>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
